import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import type {
  MixedResourcePermissionGateProps,
  PermissionState,
  RenderOptions,
} from '@/components/common/PermissionGate/types'
import {
  renderWithPermission,
  validateAsChildProps,
} from '@/components/common/PermissionGate/utils'

import { useMixedResourcePermissionCheck } from '@/hooks/permissions/usePermissionCheck'

import type { UUID } from '@/types/Id'
import type { Permission } from '@/types/Permission'

const getMixedPermissionTooltip = (
  permission: Permission | Permission[],
): string => {
  const permissionStr = Array.isArray(permission)
    ? permission.join(' and ')
    : permission

  return `You need ${permissionStr} permission on all selected items to perform this action`
}

export const MixedResourcePermissionGate = forwardRef<
  HTMLElement,
  MixedResourcePermissionGateProps
>(
  (
    {
      resources,
      permission,
      requireAll = true,
      fallback = 'hide',
      fallbackComponent,
      loadingComponent,
      tooltip,
      tooltipProps,
      tooltipContentProps,
      children,
      asChild = false,
      onPermissionChange,
    },
    ref,
  ) => {
    if (asChild) {
      validateAsChildProps(children, asChild)
    }

    const permissions = useMemo(
      () => (Array.isArray(permission) ? permission : [permission]),
      [permission],
    )

    const select = useCallback(
      (data: Map<UUID, Map<Permission, boolean>>) => {
        if (resources.length === 0) {
          return true
        }

        const resourceResults = resources.map(({ resourceId }) => {
          const resourcePermissions = data.get(resourceId)
          if (!resourcePermissions) {
            return false
          }

          const permissionChecks = permissions.map(
            (p) => resourcePermissions.get(p) === true,
          )

          return requireAll
            ? permissionChecks.every(Boolean)
            : permissionChecks.some(Boolean)
        })

        return requireAll
          ? resourceResults.every(Boolean)
          : resourceResults.some(Boolean)
      },
      [permissions, requireAll, resources],
    )

    const isEmptySelection = resources.length === 0

    const {
      data: hasPermissionFromHook,
      isLoading: isLoadingFromHook,
      isError: isErrorFromHook,
    } = useMixedResourcePermissionCheck({
      resources,
      permissions,
      enabled: !isEmptySelection,
      select,
    })

    const hasPermission = isEmptySelection
      ? true
      : hasPermissionFromHook === true
    const isLoading = isEmptySelection ? false : isLoadingFromHook
    const isError = isEmptySelection ? false : isErrorFromHook

    const prevHasPermission = useRef<boolean | null>(null)

    useEffect(() => {
      if (
        onPermissionChange &&
        !isLoading &&
        prevHasPermission.current !== hasPermission
      ) {
        prevHasPermission.current = hasPermission
        onPermissionChange(hasPermission)
      }
    }, [hasPermission, isLoading, onPermissionChange])

    const finalTooltip =
      tooltip === undefined ? getMixedPermissionTooltip(permission) : tooltip

    const permissionState: PermissionState = {
      hasPermission,
      isLoading,
      isError,
    }

    const renderOptions: RenderOptions = {
      fallback,
      fallbackComponent,
      loadingComponent,
      tooltip: finalTooltip,
      tooltipProps,
      tooltipContentProps,
    }

    const result = renderWithPermission(
      permissionState,
      children,
      renderOptions,
    )

    if (asChild && React.isValidElement(result) && ref) {
      return React.cloneElement(result, {
        ref,
        ...(result.props || {}),
      } as any)
    }

    return result
  },
)

MixedResourcePermissionGate.displayName = 'MixedResourcePermissionGate'
